import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Wraps an action (usually an API call) so it can't run twice at once.
 * Returns [run, loading]. While the action's promise is pending, further
 * calls are ignored, so double clicks don't send duplicate requests.
 * Rejections are caught here (e.g. antd form validation errors).
 */
export const useAsyncAction = (action) => {
  const [loading, setLoading] = useState(false);
  const busy = useRef(false);
  const actionRef = useRef(action);
  actionRef.current = action;

  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const run = useCallback(async (...args) => {
    if (busy.current || !actionRef.current) return;
    busy.current = true;
    setLoading(true);
    try {
      return await actionRef.current(...args);
    } catch (error) {
      // antd validation errors are already shown on the form
      if (!error?.errorFields) console.error(error);
    } finally {
      busy.current = false;
      if (mounted.current) setLoading(false);
    }
  }, []);

  return [run, loading];
};

export default useAsyncAction;
